/**
 * Las plantillas de Meta de la marca para Mensajes (R3, 29/10/2026): la lista, una sola vez para todos los grupos, y el
 * detalle de cada una (sus variables y su texto para la previa) a medida que se elige, con caché por id. Sólo con la
 * cuenta del WhatsApp oficial (`activo`): sin ella los grupos siguen con su texto y no se pide nada.
 *
 * Crear o corregir una plantilla se hace en otra pestaña (`PlantillaDelGrupo`): al volver a ésta, la lista se pide de
 * nuevo, así aparece la que se acaba de crear sin perder lo que no se guardó. «Crear las plantillas en Meta» y «Consultar
 * a Meta» la vuelven a pedir con `recargar`; el cupo de la cuenta viene con la lista.
 */

import { useCallback, useEffect, useState } from "react";
import { oficialApi } from "../../wa-oficial/api";
import { llamar } from "../../wa-oficial/components/base/comun";
import { textoError } from "../../wa-oficial/logic/respuesta";
import type { ListaPlantillas, PlantillaDetalle, PlantillaResumen } from "../../wa-oficial/types";

export function usePlantillasDeMeta(activo: boolean) {
  const [plantillas, setPlantillas] = useState<PlantillaResumen[] | null>(null);
  // cuántas plantillas usa la cuenta de WhatsApp y cuántas deja Meta: «Crear las plantillas en Meta» lo dice antes
  const [cupo, setCupo] = useState<ListaPlantillas["cupo"] | null>(null);
  const [error, setError] = useState("");
  const [detalles, setDetalles] = useState<Record<string, PlantillaDetalle>>({});
  const [errores, setErrores] = useState<Record<string, string>>({});

  const cargar = useCallback(async () => {
    const l = await llamar<ListaPlantillas>(() => oficialApi.plantillas());
    if (!l.ok) {
      setError(textoError(l));
      return;
    }
    setPlantillas(l.data?.items || []);
    setCupo(l.data?.cupo ?? null);
    setError("");
  }, []);

  useEffect(() => {
    if (!activo) return;
    void cargar();
    const alVolver = () => void cargar();
    window.addEventListener("focus", alVolver);
    return () => window.removeEventListener("focus", alVolver);
  }, [activo, cargar]);

  /** Pide el detalle de una plantilla, si todavía no está. */
  const pedirDetalle = useCallback(
    async (id: string) => {
      if (!id || detalles[id]) return;
      const l = await llamar<PlantillaDetalle>(() => oficialApi.plantilla(id));
      if (!l.ok || !l.data) {
        setErrores((x) => ({ ...x, [id]: l.ok ? "No pudimos traer la plantilla." : `No pudimos traer la plantilla. ${textoError(l)}` }));
        return;
      }
      setDetalles((x) => ({ ...x, [id]: l.data }));
      setErrores((x) => ({ ...x, [id]: "" }));
    },
    [detalles],
  );

  // `recargar`: la lista otra vez, después de crear plantillas o de «Consultar a Meta»
  return { plantillas, cupo, error, detalles, errores, pedirDetalle, recargar: cargar };
}

export type PlantillasDeMeta = ReturnType<typeof usePlantillasDeMeta>;
